import { extend } from 'umi-request';
import { history } from 'umi';
import type { ResponseError } from 'umi-request/types';
import { stringify } from 'qs';
import { getErrorMsg, onErrorNotice, camelCase, lowerCase } from 'bv-utils';
import type { Request } from 'bv-request/lib/typings'
import GlobalResponseError from 'bv-request/lib/exception';

const defaultError: string = '未知错误';

const HTTPStatusTitle = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  405: '请求方法不被允许。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

export const errorHandler = (error: ResponseError): void => {
  const { data, response } = error;
  if (response) {
    const { status, statusText } = response
    const title = HTTPStatusTitle[status] ?? statusText ?? defaultError;
    const errorData = data && typeof data === "object" ? camelCase(data) : title
    if (status === 401) {
      // 重新登录后跳转到当前 URL
      if (window.location.pathname !== '/user/login') {
        history.replace({
          pathname: '/user/login',
          search: stringify({ redirect: window.location.href }),
        });
      }
    } else if (status === 400 || status === 404) {
      throw new GlobalResponseError(errorData, data?.info ?? title);
    } else if (response.status >= 500) {
      throw new GlobalResponseError(errorData, title);
    } else {
      throw new GlobalResponseError(errorData, title);
    }
  } else {
    throw new GlobalResponseError(null, defaultError);
  }
};

// extend 新建一个 umi-request 实例
const umiRequest = extend({
  errorHandler, // 默认错误处理
  credentials: 'include', // 默认请求是否带上cookie
  timeout: 1000 * 60 * 5,
  paramsSerializer: (p: any) => stringify(p, { arrayFormat: 'comma' }),
});

// request拦截器, 改变url 或 options.
umiRequest.interceptors.request.use((url, { data, params, isLower = true, ...r }) => ( {
    url,
    options: {
      ...r,
      data: isLower ? lowerCase(data) : data,
      params: isLower ? lowerCase(params) : params,
    },
  }),
)

const request: Request = async (url, options) => {
  const { notice, isCamel = true, ...r }: any = options
  try {
    const response: any = await umiRequest(url, r);
    if (response && typeof response === 'object') {
      const { success, data, info } = response;
      if (success) {
        return [isCamel ? camelCase(data) : data, null]
      }
      const errorMsg = info ?? defaultError
      const errorRes = data ? (isCamel ? camelCase(data) : data) :errorMsg
      if (notice) {
        onErrorNotice(notice, errorMsg, undefined, defaultError)
      }
      return [ null, errorRes]
    }
    return [response, null]
  } catch (error: any) {
    if (notice) {
      onErrorNotice(notice || url.split('/api/')[1], error, undefined, defaultError)
    }
    return [null, getErrorMsg(error, defaultError)]
  }
}

export default request;
